import { Fragment, useId, useMemo, useState, type ReactNode } from "react";
import { useT } from "../i18n";
import { useTelefon } from "../schale/geraet";
import type { Punkt } from "./Handyformen";
import { Tabellenzeilen, type Telefonblatt } from "./Tabellenzeilen";
import "../stile/tabelle.css";

/* ══════════════════════════════════════════════════════════════════
   Die Tabelle — ein Baustein für jede Liste der Anwendung.

   **Warum es ihn gibt.** Am 15.09.2026 hatte die Anwendung drei
   Listenformen nebeneinander: die `.tafel` (Kunden, Ressourcen,
   Abrechnung, Preise), das Knopfraster `.vg` (Aufträge, Angebote) und
   die Karten. Verschiedene Zeilenhöhen, verschiedene Köpfe,
   verschiedenes Verhalten bei der Tastatur — und jede Maske trug ihre
   Spaltenbreiten in einem `style`-Attribut mit sich herum. Luca:
   *„wir haben tabellarisch viele unterschiedliche Ansätze, was mir
   persönlich absolut nicht gefällt. Das sollte ein Shared Component
   sein für die Tabellenstruktur, welche natürlich angepasst sind, je
   Anwendungsfall."*

   Der letzte Halbsatz ist der wichtige: **gleiche Struktur, nicht
   gleiches Aussehen um jeden Preis.** Was eine Liste besonders macht,
   steckt in ihren Spalten — eine Spalte ist eine Beschreibung, keine
   Vorlage. Was alle Listen teilen, steckt hier: Kopf, Gruppen, Klick,
   Auswahl, Sortierung, die fünf Zustände, der Überlauf und die
   gestapelte Fassung auf schmalem Schirm.

   **Was hier nicht hineingehört.** Filter, Suche und Maskenkopf bleiben
   bei der Maske. Eine Tabelle, die ihre eigene Suche mitbringt, hat
   spätestens bei der dritten Maske ein Feld zu viel oder eines zu wenig.

   Die Stile stehen in `../stile/tabelle.css` und werden von hier
   importiert, nicht aus `main.tsx`: Jede Regel dort trägt das Präfix
   `tab-`, damit ist die Stelle in der Kaskade gleichgültig.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Wo eine Spalte am Telefon steht, wenn aus der Zeile eine Karte wird.
 *
 * | Platz | was daraus wird |
 * |---|---|
 * | `titel` | die Überschrift der Karte, oben links, ohne Beschriftung |
 * | `kennung` | klein und in Mono gleich hinter dem Titel — Nummer, Kennzeichen |
 * | `marke` | oben rechts, ohne Beschriftung — der Zustand |
 * | `unter` | eine der zwei, drei Angaben darunter, die Beschriftung klein darüber |
 * | `breit` | eine eigene Zeile über die ganze Breite, Beschriftung davor — für Knöpfe und Wähler |
 * | `satz` | eine eigene Zeile, Beschriftung **darüber** — für Fließtext, der die ganze Breite braucht |
 * | `weg` | steht am Telefon nicht in der Karte; man sieht es, wenn man sie öffnet |
 *
 * **Ohne Angabe gilt eine Vorgabe** (`handyplaetze`): die `traeger`-Spalte
 * wird Titel (gibt es keine, die erste Spalte), eine Spalte mit der Klasse
 * `kennung` die Kennung. Lässt sich die Zeile antippen (`klick`), stehen
 * alle übrigen Spalten als `unter` darin; sonst als `breit`, weil in einer
 * Liste ohne Klick meist Knöpfe und Felder in den Zellen stehen.
 *
 * **Die Vorgabe lässt nichts weg.** Welche zwei, drei Angaben eine Karte
 * trägt, weiß nur die Maske — sie setzt `weg` an die Spalten, die im
 * geöffneten Datensatz ohnehin stehen, und `marke` an den Zustand. Eine
 * Vorgabe, die still Spalten verschluckt, nähme einer Liste, die ihre
 * Zeile nur aufklappt, die Angabe ganz.
 */
export type Handyplatz = "titel" | "kennung" | "marke" | "unter" | "breit" | "satz" | "weg";

/**
 * Eine Spalte.
 *
 * `kopf` ist ein **i18n-Schlüssel**, nie ein Text — er beschriftet den
 * Spaltenkopf und, in der gestapelten Fassung, jede einzelne Zelle. Ein
 * Schlüssel, zwei Orte: Es gibt keinen zweiten Text, der abweichen könnte.
 */
export type Spalte<Z> = {
  /** Eindeutig in dieser Tabelle — Schlüssel der Zelle und Merkmal beim Sortieren. */
  id: string;
  /** i18n-Schlüssel des Spaltennamens. */
  kopf: string;
  /** Wunschbreite als CSS-Länge, z. B. `"128px"`. Ohne Angabe teilt sich die Spalte den Rest. */
  breite?: string;
  /** Rechtsbündig. Für alles, was eine Zahl ist — dort zählt die letzte Stelle. */
  rechts?: boolean;
  /** Die Zelle. Bekommt die ganze Zeile, damit sie auch aus zwei Feldern eine machen kann. */
  zelle: (z: Z) => ReactNode;
  /**
   * Wonach diese Spalte sortiert. Nur Spalten mit `wert` bekommen einen
   * Sortierknopf im Kopf — ein Pfeil, der überall steht, verspricht etwas,
   * das eine Marke oder eine Ampel nicht halten kann.
   */
  wert?: (z: Z) => string | number | null | undefined;
  /**
   * Die Spalte, die die Zeile **benennt**. Gestapelt steht sie oben und
   * ohne Beschriftung: Ihr Inhalt ist die Überschrift des Blocks.
   * Höchstens eine je Tabelle.
   */
  traeger?: boolean;
  /** Klassen für die Zelle — `kennung`, `stark`, `leise`, `zahl`, `zeit`, `geld`. */
  klasse?: string;
  /**
   * Wo die Spalte am Telefon in der Karte steht (`Handyplatz`). Ohne
   * Angabe gilt die Vorgabe aus `handyplaetze`.
   *
   *     { id: "status", kopf: "…", zelle: …, handy: "marke" }
   *
   * **`satz` ist der Unterschied zwischen einem Wert und einem Text**
   * (17.09.2026). `breit` stellt die Beschriftung davor und gibt ihr 38 %
   * der Zeile — richtig für einen Knopf oder einen Wähler, falsch für
   * „Nachdem er hier angelegt ist. Interessenten bleiben hier": Der Satz
   * lief im Rest in sieben Zeilen. Bei `satz` steht die Beschriftung klein
   * darüber und der Text bekommt die ganze Breite. Das Info-Blatt und die
   * Textbausteine haben sich das bis heute maskenseitig geholt.
   */
  handy?: Handyplatz;
};

/** Eine Gruppe mit ihrer Zwischenüberschrift. */
export type Gruppe<Z> = {
  id: string;
  /** Die Überschrift — schon übersetzt oder ein Stück Markup. */
  kopf: ReactNode;
  /** Die Zahl dahinter. Ohne Angabe die Länge von `zeilen`. */
  zahl?: number;
  zeilen: Z[];
};

/** Die Auswahl liegt bei der Maske, nicht in der Tabelle: Sie weiß, was danach damit geschieht. */
export type Auswahl = {
  gewaehlt: ReadonlySet<string>;
  /** Eine Zeile an oder aus. */
  umschalten: (id: string, an: boolean) => void;
  /** Alle sichtbaren an oder aus. Fehlt sie, gibt es kein Kästchen im Kopf. */
  alle?: (ids: string[], an: boolean) => void;
};

type Inhalt<Z> =
  | { zeilen: Z[]; gruppen?: undefined }
  | { gruppen: Gruppe<Z>[]; zeilen?: undefined };

export type TabelleEigenschaften<Z> = Inhalt<Z> & {
  spalten: Spalte<Z>[];
  /** Der Schlüssel je Zeile — stabil, nicht der Index. */
  id: (z: Z) => string;
  /** i18n-Schlüssel für das `aria-label` der Tabelle. Ein Vorleser sagt sonst nur „Tabelle". */
  schild: string;
  /** Zeile öffnen. Mit Klick, Enter und Leertaste — dasselbe für Maus und Tastatur. */
  klick?: (z: Z) => void;
  /** Ist diese Zeile gewählt? Fläche plus gelbe Kante, zwei Träger. */
  gewaehlt?: (z: Z) => boolean;
  /** Zusätzliche Klassen je Zeile — `storniert`, `frisch`, `markiert`. */
  zeilenklasse?: (z: Z) => string | undefined;
  /**
   * Was unter der Zeile steht, wenn sie aufgeklappt ist — über die ganze
   * Breite. Gibt die Funktion `null`, gibt es keine Unterzeile.
   *
   * Die Abrechnung braucht das: Warum ein Beleg rot ist, steht nicht in
   * fünf Spalten, sondern in einem Absatz mit Prüfungen und Rechenweg.
   * Ihn in ein Blatt an der Kante zu legen hieße, für jeden der acht
   * Belege einmal hin und zurück.
   */
  unterzeile?: (z: Z) => ReactNode | null;
  /** Was ein Vorleser zur Zeile sagt, bevor er sie vorliest. i18n-Schlüssel plus Werte. */
  zeilenschild?: (z: Z) => { k: string; werte: Record<string, string | number> };
  auswahl?: Auswahl;
  /** 46 px statt 36 px. Für Listen mit zwei Zeilen Text je Position. */
  luftig?: boolean;
  /** Lädt: Die Zeilenform bleibt stehen, grau gefüllt — kein Kreisel. */
  laedt?: boolean;
  /** Leer: ein Satz, der sagt was fehlt, und der Knopf, der es anlegt. Der Spaltenkopf steht dann nicht. */
  leer?: ReactNode;
  /** Summe, Erklärung, Zähler — im Blatt, nicht darunter. */
  fuss?: ReactNode;
  /**
   * Zeilen, die unter der Liste stehen und nicht aus `zeilen` kommen: die
   * **Eingabezeile**.
   *
   * Eine Position anzulegen ist eine Zeile in der Tabelle und kein Formular
   * daneben (16.09.2026). Ohne diesen Platz müsste die Maske ihre eigene
   * Zeilenform neben das Blatt bauen — und zwei Formen für dieselbe Zeile
   * laufen auseinander, bis eine von beiden die schlechtere ist.
   *
   * Erwartet `<tr>`-Elemente; sie tragen ihre Rollen selbst, wie alle
   * Tabellenteile hier. Solange etwas darin steht, gilt die Tabelle **nicht
   * als leer**: Der Spaltenkopf über einer Eingabezeile ist genau das, was
   * ihre Felder beschriftet.
   */
  anhang?: ReactNode;
  /** Unter dieser Breite scrollt der **Rahmen** waagerecht, nie die Seite. */
  mindestbreite?: number;
  /** Wie viele Zeilenformen der Ladezustand zeigt. */
  ladezeilen?: number;
  /**
   * Am Telefon wird jede Zeile eine Karte (`handy` an der Spalte). Wer
   * seine Zeile dort selbst ordnet — die Positionsliste mit ihrer
   * Rechenzeile —, schaltet das mit `karte={false}` ab und behält die
   * gestapelte Fassung samt seinen eigenen Regeln.
   */
  karte?: boolean;
  /**
   * **Unter 761 px bleibt die Tabelle eine Tabelle**, wenn `stapeln`
   * `false` ist — Spalten nebeneinander, bei Bedarf rollt der Rahmen
   * waagerecht.
   *
   * Der Regelfall ist das Stapeln: Sechs Spalten passen auf 390 px nicht
   * nebeneinander. Es gibt aber Tafeln, deren **Aussage** der Vergleich von
   * Spalte zu Spalte ist — die Preisstaffel ist die eine: „1–2 Tage · 45,00
   * · € je Tag" gestapelt ergibt drei Zeilen und eine Beschriftung
   * dazwischen, und fünf Stufen stehen dann nicht mehr vergleichbar da.
   *
   * Es gilt für die Tabelle **und für Tabellen darin**: Eine von Hand
   * gebaute Tafel in einer `unterzeile` erbt sonst die Stapelregeln der
   * äußeren (die Belegzeilen der Abrechnung). Sie schreibt dafür selbst
   * `data-stapeln="nein"` an ihr `<table>`.
   */
  stapeln?: boolean;
  /**
   * Die Ampel einer Zeile am Telefon (19.09.2026) — der Punkt vorn im
   * `Zweizeiler` (`bausteine/Tabellenzeilen.tsx`). Ohne Angabe keiner.
   */
  punkt?: (z: Z) => Punkt;
  /**
   * Am Telefon Zeilen statt Karten, **auch ohne `klick`** (19.09.2026): Die
   * Bedienspalten (Wähler, Schalter) stehen dann im Blatt, das die Zeile
   * öffnet (`Tabellenzeilen.tsx`, „Der Blattmodus"). `unter` fasst den Stand
   * für die zweite Zeile zusammen.
   */
  telefonblatt?: Telefonblatt<Z>;
};

/** Zwei Werte vergleichen — Zahlen als Zahlen, Text nach deutscher Sortierung, Leeres ans Ende. */
function vergleichen(a: unknown, b: unknown): number {
  const leerA = a === null || a === undefined || a === "";
  const leerB = b === null || b === undefined || b === "";
  if (leerA && leerB) return 0;
  if (leerA) return 1;
  if (leerB) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "de", { numeric: true, sensitivity: "base" });
}

/**
 * Der Platz jeder Spalte am Telefon — die Angabe der Maske, sonst die Vorgabe
 * (siehe `Handyplatz`).
 */
export function handyplaetze<Z>(spalten: Spalte<Z>[], tippbar: boolean): Record<string, Handyplatz> {
  const plaetze: Record<string, Handyplatz> = {};
  const hatTitel = spalten.some((s) => s.handy === "titel" || (s.handy === undefined && s.traeger));
  spalten.forEach((s, i) => {
    let platz = s.handy;
    if (!platz) {
      if (s.traeger || (!hatTitel && i === 0)) platz = "titel";
      else if ((s.klasse ?? "").split(/\s+/).includes("kennung")) platz = "kennung";
      else platz = tippbar ? "unter" : "breit";
    }
    plaetze[s.id] = platz;
  });
  return plaetze;
}

export function Tabelle<Z>({
  spalten, zeilen, gruppen, id, schild,
  klick, gewaehlt, zeilenklasse, zeilenschild, auswahl, unterzeile,
  luftig, laedt, leer, fuss, anhang, mindestbreite, ladezeilen = 8, karte = true,
  stapeln = true, punkt, telefonblatt,
}: TabelleEigenschaften<Z>) {
  const t = useT();
  const telefon = useTelefon();
  const wahlId = useId();
  const plaetze = useMemo(() => handyplaetze(spalten, Boolean(klick)), [spalten, klick]);

  /* Sortiert wird in der Tabelle, nicht in der Maske. Drei Zustände, und
     der dritte ist wichtig: Wer zum zweiten Mal auf denselben Kopf klickt,
     dreht um; beim dritten Mal steht wieder die Reihenfolge da, in der die
     Maske die Zeilen geliefert hat. Bei „Gruppiert nach Zustand, nicht nach
     Datum" ist genau die die Aussage der Liste — sie muss zurückzuholen
     sein. */
  const [sortiert, setzeSortiert] = useState<{ nach: string; ab: boolean } | null>(null);

  const ordnen = useMemo(() => {
    if (!sortiert) return (liste: Z[]) => liste;
    const spalte = spalten.find((s) => s.id === sortiert.nach);
    if (!spalte?.wert) return (liste: Z[]) => liste;
    const hol = spalte.wert;
    const richtung = sortiert.ab ? -1 : 1;
    return (liste: Z[]) => [...liste].sort((a, b) => richtung * vergleichen(hol(a), hol(b)));
  }, [sortiert, spalten]);

  const weiter = (sid: string) =>
    setzeSortiert((alt) =>
      !alt || alt.nach !== sid ? { nach: sid, ab: false } : alt.ab ? null : { nach: sid, ab: true },
    );

  /* Die Blöcke: ohne Gruppen genau einer ohne Überschrift. Die Sortierung
     greift INNERHALB der Gruppe — eine Gruppierung, die beim Sortieren
     zerfällt, war keine. */
  const bloecke: Gruppe<Z>[] = useMemo(() => {
    const roh = gruppen ?? [{ id: "alle", kopf: null, zeilen: zeilen ?? [] }];
    return roh.map((g) => ({ ...g, zeilen: ordnen(g.zeilen) }));
  }, [gruppen, zeilen, ordnen]);

  const alleIds = useMemo(
    () => bloecke.flatMap((g) => g.zeilen.map(id)),
    [bloecke, id],
  );

  /* ── Am Telefon: Zeilen statt Karten (19.09.2026) ──
     Eine Tabelle, die sich antippen lässt, zeichnet am Telefon gleich hohe
     `Zweizeiler`, und was sie unter einer Zeile aufklappt, steht im Blatt
     von unten (`Tabellenzeilen.tsx` sagt warum). Wer seine Zeile selbst
     ordnet (`karte={false}`), Kästchen trägt oder eine Eingabezeile, bleibt
     bei der gestapelten Fassung. */
  if (telefon && karte && (klick || telefonblatt) && !auswahl && !anhang) {
    return (
      <Tabellenzeilen
        spalten={spalten}
        plaetze={plaetze}
        bloecke={bloecke}
        id={id}
        klick={klick}
        telefonblatt={telefonblatt}
        unterzeile={unterzeile}
        punkt={punkt}
        laedt={laedt}
        leerSatz={leer}
        fuss={fuss}
        ladezeilen={Math.min(ladezeilen, 6)}
        schild={schild}
      />
    );
  }
  const anzahl = alleIds.length;
  const spaltenzahl = spalten.length + (auswahl ? 1 : 0);

  /* ── Leer ──
     Nie eine leere Tabelle mit Kopfzeile: Sie sagt dann nur, was alles
     nicht da ist. Der Rahmen bleibt trotzdem stehen — der Satz gehört auf
     das Blatt, nicht auf den Tisch.

     Mit einer Eingabezeile (`anhang`) ist die Tabelle nicht leer, auch wenn
     noch keine Zeile gespeichert ist: Der Spaltenkopf beschriftet dann die
     Felder, in die gerade jemand schreibt. */
  if (!laedt && anzahl === 0 && leer && !anhang) {
    return (
      <div className="tab-rahmen">
        <div className="tab-fenster"><div className="tab-leer">{leer}</div></div>
        {fuss ? <div className="tab-fuss">{fuss}</div> : null}
      </div>
    );
  }

  const alleGewaehlt = Boolean(auswahl) && anzahl > 0
    && alleIds.every((x) => auswahl?.gewaehlt.has(x));

  /* ── Sortieren, wo der Spaltenkopf fehlt ──
     Gestapelt ist der Spaltenkopf aus dem Bild — und mit ihm jeder
     Sortierknopf. Statt ihn nachzubauen, steht darüber ein Wähler: ein
     natives `select`, das das Telefon als eigene Liste aufklappt und das
     ein Daumen trifft. Er setzt denselben Zustand wie der Kopf; wer das
     Fenster schmal zieht, sieht dieselbe Reihenfolge. Ob er zu sehen ist,
     sagt der Stil (`tabelle.css`: wo die Tabelle stapelt) — gebaut wird er
     immer, sonst sprünge die Liste beim Ziehen des Fensters. Die dritte
     Stellung („wie geliefert") steht zuerst, weil sie die Aussage der
     Liste ist. */
  const sortierbar = spalten.filter((s) => s.wert);
  const sortierwahl = sortierbar.length > 0 && !laedt ? (
    <div className="tab-ordnen">
      <label htmlFor={wahlId}>{t("tabelle.sortieren")}</label>
      <select
        id={wahlId}
        className="feld"
        value={sortiert ? `${sortiert.nach}:${sortiert.ab ? "ab" : "auf"}` : ""}
        onChange={(e) => {
          const [nach, richtung] = e.target.value.split(":");
          setzeSortiert(nach ? { nach, ab: richtung === "ab" } : null);
        }}
      >
        <option value="">{t("tabelle.sortierungListe")}</option>
        {sortierbar.map((s) => (
          <Fragment key={s.id}>
            <option value={`${s.id}:auf`}>{t("tabelle.sortiertAuf", { spalte: t(s.kopf) })}</option>
            <option value={`${s.id}:ab`}>{t("tabelle.sortiertAb", { spalte: t(s.kopf) })}</option>
          </Fragment>
        ))}
      </select>
    </div>
  ) : null;

  return (
    <div className="tab-rahmen">
      {sortierwahl}
      <div className="tab-fenster">
        <table
          role="table"
          className={`tab${luftig ? " luftig" : ""}`}
          {...(stapeln ? {} : { "data-stapeln": "nein" })}
          aria-label={t(schild)}
          aria-busy={laedt ? true : undefined}
          style={mindestbreite ? { minWidth: mindestbreite } : undefined}
        >
          {/* Die Breiten stehen hier, einmal — nicht als `style` an jeder Zeile
              in jeder Maske. Das war der Weg, auf dem dieselbe Liste in zwei
              Masken um 14 px auseinanderlief. */}
          <colgroup>
            {auswahl ? <col style={{ width: 38 }} /> : null}
            {spalten.map((s) => (
              <col key={s.id} style={s.breite ? { width: s.breite } : undefined} />
            ))}
          </colgroup>

          <thead role="rowgroup">
            <tr role="row">
              {auswahl ? (
                <th role="columnheader" className="tab-wahl" data-kopf={t("tabelle.auswahl")}>
                  {auswahl.alle ? (
                    <input
                      type="checkbox"
                      checked={alleGewaehlt}
                      aria-label={t(alleGewaehlt ? "tabelle.alleAbwaehlen" : "tabelle.alleWaehlen")}
                      onChange={(e) => auswahl.alle?.(alleIds, e.target.checked)}
                    />
                  ) : null}
                </th>
              ) : null}
              {spalten.map((s) => {
                const dran = sortiert?.nach === s.id;
                return (
                  <th
                    role="columnheader"
                    key={s.id}
                    className={s.rechts ? "r" : undefined}
                    aria-sort={dran ? (sortiert.ab ? "descending" : "ascending") : undefined}
                  >
                    {s.wert ? (
                      <button
                        type="button"
                        className="tab-sort"
                        onClick={() => weiter(s.id)}
                        aria-label={t(
                          !dran ? "tabelle.sortierenAuf" : sortiert.ab ? "tabelle.sortierungAus" : "tabelle.sortierenAb",
                          { spalte: t(s.kopf) },
                        )}
                      >
                        {t(s.kopf)}
                        <span className="tab-sort-pfeil" aria-hidden="true">
                          {dran && sortiert.ab ? "▼" : "▲"}
                        </span>
                      </button>
                    ) : (
                      t(s.kopf)
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody role="rowgroup">
            {/* ── Lädt ──
                Die Zeilenform bleibt stehen, grau gefüllt. Ein Kreisel an
                dieser Stelle lässt die Seite springen, sobald die Daten da
                sind (28-designkonzept.md, „Die fünf Zustände"). */}
            {laedt
              ? Array.from({ length: ladezeilen }, (_, i) => (
                  <tr role="row" className="tab-skelett" key={`laedt-${i}`} aria-hidden="true">
                    {auswahl ? <td role="cell" className="tab-wahl" /> : null}
                    {spalten.map((s, k) => (
                      <td role="cell" key={s.id}>
                        <span className="tab-balken" style={{ width: `${[68, 44, 52, 38, 46, 34][k % 6]}%` }} />
                      </td>
                    ))}
                  </tr>
                ))
              : bloecke.map((g) => (
                  <Fragment key={g.id}>
                    {g.kopf ? (
                      <tr role="row" className="tab-gruppe">
                        <th role="columnheader" scope="colgroup" colSpan={spaltenzahl}>
                          {g.kopf}
                          <span className="tab-gruppe-zahl">{g.zahl ?? g.zeilen.length}</span>
                        </th>
                      </tr>
                    ) : null}
                    {g.zeilen.map((z) => {
                      const zid = id(z);
                      const schildZeile = zeilenschild?.(z);
                      const klassen = [
                        klick ? "klick" : "",
                        gewaehlt?.(z) ? "gewaehlt" : "",
                        zeilenklasse?.(z) ?? "",
                      ].filter(Boolean).join(" ");
                      const zeile = (
                        <tr
                          role="row"
                          key={zid}
                          id={`tab-${zid}`}
                          className={klassen || undefined}
                          /* Die Zeile bleibt eine Zeile. Ein `role="button"`
                             darauf nimmt der Tabelle ihre Struktur, und ein
                             Vorleser zählt dann keine Spalten mehr — Fokus und
                             Tastatur gehen auch ohne. */
                          {...(klick
                            ? {
                                tabIndex: 0,
                                onClick: () => klick(z),
                                onKeyDown: (e: React.KeyboardEvent) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    klick(z);
                                  }
                                },
                              }
                            : {})}
                          {...(schildZeile ? { "aria-label": t(schildZeile.k, schildZeile.werte) } : {})}
                          {...(gewaehlt ? { "aria-selected": gewaehlt(z) } : {})}
                        >
                          {auswahl ? (
                            <td role="cell" className="tab-wahl" data-kopf={t("tabelle.auswahl")}>
                              <input
                                type="checkbox"
                                checked={auswahl.gewaehlt.has(zid)}
                                aria-label={t("tabelle.zeileWaehlen")}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => auswahl.umschalten(zid, e.target.checked)}
                              />
                            </td>
                          ) : null}
                          {spalten.map((s) => (
                            <td
                              role="cell"
                              key={s.id}
                              data-kopf={t(s.kopf)}
                              {...(karte ? { "data-handy": plaetze[s.id] } : {})}
                              {...(s.traeger ? { "data-traeger": "ja" } : {})}
                              className={[s.rechts ? "r" : "", s.klasse ?? ""].filter(Boolean).join(" ") || undefined}
                            >
                              {s.zelle(z)}
                            </td>
                          ))}
                        </tr>
                      );
                      const auf = unterzeile?.(z) ?? null;
                      if (!auf) return zeile;
                      return (
                        <Fragment key={`${zid}-auf`}>
                          {zeile}
                          <tr role="row" className="tab-unterzeile">
                            <td role="cell" colSpan={spaltenzahl}>{auf}</td>
                          </tr>
                        </Fragment>
                      );
                    })}
                  </Fragment>
                ))}
            {/* Die Eingabezeile steht unter der Liste — in der Tabelle, nicht
                daneben. Sie bringt ihre eigenen `<tr>` mit. */}
            {anhang}
          </tbody>
        </table>
      </div>
      {fuss ? <div className="tab-fuss">{fuss}</div> : null}
    </div>
  );
}

/**
 * Die Sache in der ersten Spalte: oben der Name, darunter kleiner Nummer
 * und Gegenstand.
 *
 * Eine Tabelle mit sechs gleichwertigen Spalten liest sich wie ein
 * Datenblatt. Ein Auftrag ist aber eine Sache mit einem Namen — also
 * trägt die erste Spalte ihn, und alles Weitere ordnet sich darunter.
 * Das ist der eine Teil der alten Vorgangsliste, der es wert war zu
 * bleiben.
 */
export function Sache({ name, nr, dazu }: { name: ReactNode; nr?: ReactNode; dazu?: ReactNode }) {
  return (
    <span className="tab-sache">
      <b>{name}</b>
      {nr || dazu ? (
        <span className="bei">
          {nr ? <span className="nr">{nr}</span> : null}
          {dazu ? <span>{dazu}</span> : null}
        </span>
      ) : null}
    </span>
  );
}

/** Der Rahmen um eine Tabelle, die in einer scrollenden Maske steht. */
export function Tabellenraum({ children }: { children: ReactNode }) {
  return <div className="tab-raum">{children}</div>;
}
